// One entry point on the Entry points tab (spec 11 6.15.10, mock n05): the header with its kind, add-on chips and edge
// host; Domains and Routes from the status endpoint; the pending origins, certificate access and rotation callouts.
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
  Loader,
  Lock,
  MoreHorizontal,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { useApi } from '../../api/context';
import { queryKeys, useEntryPointStatus } from '../../api/queries';
import type {
  EdgeKind,
  EntryPointDomainStatus,
  EntryPointRouteStatus,
  EntryPointSettings,
  EntryPointStatus,
  Network,
} from '../../api/types';
import { useToast } from '../../components/toast';
import { CopyButton, ErrorMessage, Loading } from '../../components/ui';
import { formatRelative } from '../../lib/format';
import { MenuButton, type MenuEntry } from '../../shell/Menu';
import { strings } from '../../strings';
import { addOnChips, addOnEnabled, dayWords, hostPaths, inactiveWords, kindLine, vaultOf } from './helpers';

const e = strings.networks.entryPoints;

function DomainState({
  domain,
  lastKnownAt,
  network,
}: {
  domain: EntryPointDomainStatus;
  lastKnownAt: string | null;
  network: string;
}) {
  const cert = domain.certificate === 'managed' ? e.managedCertificate : e.keyVaultCertificate;
  switch (domain.state) {
    // Feature spec 17, phase 5 (CloudFront): issued, served after the next network apply.
    case 'waiting-for-apply':
      return (
        <span className="badge tone-info whitespace-normal">
          <Clock aria-hidden className="size-3.5 shrink-0" />
          {e.aws.validatedApply(network)}
        </span>
      );
    case 'validated':
      return (
        <span className="badge tone-success">
          <CircleCheck aria-hidden className="size-3.5" />
          {e.domainState.validated} · {cert}
        </span>
      );
    case 'waiting-for-dns':
      return (
        <span className="badge tone-warning">
          <Clock aria-hidden className="size-3.5" />
          {e.domainState['waiting-for-dns']}
        </span>
      );
    case 'failed':
      return (
        <span className="badge tone-danger">
          <CircleX aria-hidden className="size-3.5" />
          {e.domainState.failed}
        </span>
      );
    case 'unknown':
      return (
        <span className="badge tone-neutral">
          <CircleDashed aria-hidden className="size-3.5" />
          {e.domainState.unknown}
          {lastKnownAt ? ` · ${e.lastKnown(formatRelative(lastKnownAt))}` : ''}
        </span>
      );
    default:
      return (
        <span className="badge tone-neutral">
          <CircleDashed aria-hidden className="size-3.5" />
          {e.domainState.pending}
        </span>
      );
  }
}

function DomainNext({
  domain,
  zone,
  gcp = false,
}: {
  domain: EntryPointDomainStatus;
  zone?: string | null;
  /** Feature spec 17, phase 7: the Google Cloud load balancer's A record and authorization CNAME stay listed. */
  gcp?: boolean;
}) {
  if (gcp && domain.state !== 'pending' && domain.records.length)
    return (
      <ul className="space-y-1">
        {domain.records.map((r) => (
          <li key={`${r.type}-${r.name}`} className="flex flex-wrap items-center gap-1">
            <span className="min-w-0 break-all">
              <span className="text-fg-muted">{r.type === 'A' ? e.gcp.aRecord : e.gcp.authorization}:</span>{' '}
              {e.createVerb} {r.type} <code className="font-mono text-[12px]">{r.name}</code> ={' '}
              <code className="font-mono text-[12px]">{r.value}</code>
            </span>
            <CopyButton value={r.value} label={e.copyRecord(r.type, r.name)} />
          </li>
        ))}
        {domain.state === 'validated' && <li className="text-fg-muted">{e.renews}</li>}
      </ul>
    );
  if (domain.state === 'validated' && domain.certificate === 'managed')
    return <span className="text-fg-muted">{e.renews}</span>;
  if (domain.state !== 'waiting-for-dns' && domain.state !== 'failed' && domain.state !== 'waiting-for-apply')
    return null;
  return (
    <ul className="space-y-1">
      {zone && (
        <li className="text-fg-muted" data-testid="ep-domain-zone">
          {e.aws.recordsByNebisys} <code className="font-mono text-[12px]">{zone}</code>
        </li>
      )}
      {domain.records.map((r) => (
        <li key={`${r.type}-${r.name}`} className="flex flex-wrap items-center gap-1">
          <span className="min-w-0 break-all">
            {e.createVerb} {r.type} <code className="font-mono text-[12px]">{r.name}</code> ={' '}
            <code className="font-mono text-[12px]">{r.value}</code>
          </span>
          <CopyButton value={r.value} label={e.copyRecord(r.type, r.name)} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Feature spec 17, phase 8 (spec 11 6.15 item 39): a Cloudflare domain's state: the proxied record Nebisys wrote into its
 * zone (no records to copy), "Waiting for an origin" while no active route serves it, else pending.
 */
function CloudflareDomainState({
  domain,
  routes,
}: {
  domain: EntryPointDomainStatus;
  routes: readonly EntryPointRouteStatus[];
}) {
  if (domain.zone?.recordId)
    return (
      <span className="badge tone-success whitespace-normal" data-testid={`ep-cf-record-${domain.hostname}`}>
        <CircleCheck aria-hidden className="size-3.5 shrink-0" />
        {e.cloudflare.proxiedRecord(domain.zone.zoneId)}
      </span>
    );
  const active = routes.some((r) => r.originState !== 'inactive' && r.hostnames.includes(domain.hostname));
  return (
    <span className="badge tone-neutral whitespace-normal" data-testid={`ep-cf-record-${domain.hostname}`}>
      <CircleDashed aria-hidden className="size-3.5 shrink-0" />
      {active ? e.domainState.pending : e.cloudflare.waitingForOrigin}
    </span>
  );
}

function Health({
  route,
  network,
  pending,
  messages = [],
  cloudflare = false,
}: {
  route: EntryPointRouteStatus;
  network: Pick<Network, 'name'>;
  /** Whether the route's origin deployment still has to be applied (`pendingOrigins`). */
  pending?: boolean;
  /** The status `messages`: a Google Cloud route's waiting text ("Apply orders-prod, then Production, to serve route shop."). */
  messages?: readonly string[];
  /** Feature spec 17, phase 8: a Cloudflare route ("Apply Production to serve route shop.", not probed). */
  cloudflare?: boolean;
}) {
  // Feature spec 17, phase 7 (spec 04 6.14.10): the load balancer serves a route once the network's apply declared it;
  // phase 8: Cloudflare likewise.
  const gcpWait =
    route.access === 'serverless-neg' || cloudflare
      ? messages.find((m) => m.endsWith(` route ${route.routeId}.`))?.replace(/\.$/, '')
      : undefined;
  if (route.originState === 'waiting-for-apply')
    return (
      <span className="badge tone-neutral whitespace-normal" data-testid={`ep-route-wait-${route.routeId}`}>
        <Loader aria-hidden className="size-3.5 shrink-0" />
        {gcpWait
          ? gcpWait
          : route.access !== 'vpc-origin'
            ? e.waitingForApply
            : pending
              ? // Feature spec 17, phase 5: a VPC origin needs the deployment's apply, then a network apply.
                e.aws.waitingVpcOrigin(route.origin?.deploymentName ?? '')
              : e.aws.waitingNetworkApply(network.name)}
      </span>
    );
  if (route.originState === 'inactive')
    return (
      <span className="badge tone-warning whitespace-normal">
        <TriangleAlert aria-hidden className="size-3.5 shrink-0" />
        {e.inactive(inactiveWords(route.inactiveReason, route.origin?.deploymentName ?? '', network.name))}
      </span>
    );
  if (route.originState === 'not-applied')
    return (
      <span className="badge tone-neutral">
        <CircleDashed aria-hidden className="size-3.5" />
        {e.notAppliedRoute}
      </span>
    );
  switch (route.health) {
    case 'healthy':
      return (
        <span className="badge tone-success">
          <CircleCheck aria-hidden className="size-3.5" />
          {e.healthWords.healthy}
        </span>
      );
    case 'unhealthy':
      return (
        <span className="badge tone-danger">
          <CircleX aria-hidden className="size-3.5" />
          {e.healthWords.unhealthy}
        </span>
      );
    case 'not-probed': {
      const hint = cloudflare ? e.cloudflare.notProbedHint : e.notProbedHint;
      return (
        <span className="badge tone-neutral" title={hint}>
          <CircleDashed aria-hidden className="size-3.5" />
          {e.healthWords['not-probed']}
          <span className="sr-only">: {hint}</span>
        </span>
      );
    }
    default:
      return (
        <span className="badge tone-neutral">
          <CircleDashed aria-hidden className="size-3.5" />
          {e.healthWords.unknown}
        </span>
      );
  }
}

function Access({ route }: { route: EntryPointRouteStatus }) {
  const locked = route.strength === 'strongest' || route.strength === 'strong';
  return locked ? (
    <span className="inline-flex items-start gap-1">
      <Lock aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      {route.accessText}
    </span>
  ) : (
    <span className="inline-flex items-start gap-1 text-[var(--warning)]">
      <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      {route.accessText}
    </span>
  );
}

// Feature spec 17, step 8b (spec 11 item 39): below Cloudflare Enterprise, the route's host names as custom domains on the origin.
function OriginDomain({ route }: { route: EntryPointRouteStatus }) {
  const domain = route.originDomain;
  if (!domain) return null;
  const served = domain.state === 'served';
  return (
    <span
      className={`mt-1 flex items-start gap-1 text-[12.5px] ${served ? 'text-[var(--success)]' : 'text-fg-muted'}`}
      data-testid={`ep-route-domain-${route.routeId}`}
      data-state={domain.state}
    >
      {served ? (
        <CircleCheck aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      ) : (
        <Loader aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      )}
      <span>
        <span className="font-semibold">
          {served ? e.cloudflare.originDomainServed : e.cloudflare.originDomainAdding}
        </span>
        {!served && <span className="block">{domain.text}</span>}
      </span>
    </span>
  );
}

function Origin({ route }: { route: EntryPointRouteStatus }) {
  if (!route.origin) return <span className="text-fg-muted">–</span>;
  const o = route.origin;
  const cloud = o.provider && o.provider !== 'azure' ? e.crossCloud[o.provider] : null;
  return (
    <span>
      <code className="font-mono text-[12px] font-semibold">
        {o.deploymentName ?? o.deploymentId} / {o.resourceId}
      </code>{' '}
      <span className="text-[12px] text-fg-muted">
        {[o.platformLabel, cloud].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

function rotationWords(step: EntryPointStatus['rotations'][number], network: string) {
  switch (step.step) {
    case 'requested':
      return e.rotationStep.requested(network);
    case 'switching':
      return e.rotationStep.switching(network);
    case 'dropping':
      return e.rotationStep.dropping(network);
    default:
      return null;
  }
}

/** The callouts under the tables: pending origins, certificate access, rotations, a failed read. */
function Callouts({ status, network }: { status: EntryPointStatus; network: Pick<Network, 'name'> }) {
  const deploymentLink = (d: { deploymentId: string; deploymentName: string }) => (
    <Link key={d.deploymentId} className="link underline" to={`/deployments/${d.deploymentId}`}>
      {d.deploymentName}
    </Link>
  );
  const join = (items: { deploymentId: string; deploymentName: string }[]) =>
    items.map((d, i) => (
      <Fragment key={d.deploymentId}>
        {i > 0 && ', '}
        {deploymentLink(d)}
      </Fragment>
    ));
  const [before, after] = e.pendingOrigins('\u0000').split('\u0000');
  return (
    <>
      {status.lastKnownAt && (
        <p className="callout callout-warning" data-testid="ep-stale">
          <TriangleAlert aria-hidden className="size-4" />
          <span>
            {e.stale(formatRelative(status.lastKnownAt))} {status.messages.join(' ')}
          </span>
        </p>
      )}
      {!status.lastKnownAt && status.messages.length > 0 && (
        // Feature spec 17, phase 5: what the next network apply does ("Apply Production to add route shop.").
        <p className="callout callout-info" data-testid="ep-messages">
          <Clock aria-hidden className="size-4" />
          <span>{status.messages.join(' ')}</span>
        </p>
      )}
      {status.pendingOrigins.length > 0 && (
        <p className="callout callout-warning" data-testid="ep-pending-origins">
          <TriangleAlert aria-hidden className="size-4" />
          <span>
            {before}
            {join(status.pendingOrigins)}
            {after}
          </span>
        </p>
      )}
      {status.certificateAccess?.state === 'pending' && (
        <div className="callout callout-warning" data-testid="ep-certificate-access">
          <TriangleAlert aria-hidden className="size-4" />
          <span className="flex flex-wrap items-center gap-1">
            {e.certificateAccess(
              vaultOf(status.certificateAccess.secretIds[0]),
              status.certificateAccess.identityPrincipalId ?? '',
              network.name,
            )}
            {status.certificateAccess.identityPrincipalId && (
              <CopyButton value={status.certificateAccess.identityPrincipalId} label={e.copyPrincipal} />
            )}
          </span>
        </div>
      )}
      {status.rotations.map((r) => (
        <p key={r.routeId} className="callout callout-info" data-testid={`ep-rotation-${r.routeId}`}>
          <Clock aria-hidden className="size-4" />
          <span>
            {e.rotation(r.routeId)}{' '}
            {r.step === 'delivering' ? (
              <>
                {e.rotationStep.delivering} {join(r.blockedBy)} {e.rotationStep.deliveringAfter}
              </>
            ) : (
              rotationWords(r, network.name)
            )}
          </span>
        </p>
      ))}
    </>
  );
}

function Tables({
  status,
  network,
  ep,
}: {
  status: EntryPointStatus;
  network: Pick<Network, 'name'>;
  ep: EntryPointSettings;
}) {
  const pending = new Set(status.pendingOrigins.flatMap((p) => p.routeIds));
  const gcp = status.kind === 'gcp-global-load-balancer';
  const cloudflare = status.kind === 'cloudflare';
  return (
    <>
      {(status.addresses?.length ?? 0) > 0 && (
        // Feature spec 17, phase 5: AWS Global Accelerator's static IPs; phase 7: the Google Cloud load balancer's address.
        <p className="net-ep-ips text-[13px]" data-testid="ep-static-ips">
          <span className="font-semibold">{gcp ? e.gcp.ipAddress : e.aws.staticIps}:</span>
          {status.addresses!.map((ip) => (
            <span key={ip} className="inline-flex items-center gap-1">
              <code className="font-mono text-[12.5px]">{ip}</code>
              <CopyButton value={ip} label={gcp ? e.gcp.copyIp(ip) : e.aws.copyStaticIp(ip)} />
            </span>
          ))}
        </p>
      )}
      <h3 className="net-ep-heading">{e.domains}</h3>
      {status.kind === 'aws-global-accelerator' ? (
        <p className="help">{e.aws.passthrough}</p>
      ) : status.domains.length === 0 ? (
        <p className="help">{status.hostName ? status.hostName : e.appliesFirst}</p>
      ) : (
        <table className="table table-cards net-ep-table">
          <caption className="sr-only">{e.domainsCaption(status.entryPointId)}</caption>
          <thead>
            <tr>
              <th scope="col">{e.hostName}</th>
              <th scope="col">{e.state}</th>
              <th scope="col">{e.next}</th>
            </tr>
          </thead>
          <tbody>
            {status.domains.map((d) => (
              <tr key={d.hostname} data-testid={`ep-domain-${d.hostname}`}>
                <td data-label={e.hostName}>
                  <code className="font-mono text-[12.5px]">{d.hostname}</code>
                </td>
                <td data-label={e.state}>
                  {cloudflare ? (
                    <CloudflareDomainState domain={d} routes={status.routes} />
                  ) : (
                    <DomainState domain={d} lastKnownAt={status.lastKnownAt} network={network.name} />
                  )}
                </td>
                <td data-label={e.next}>
                  {cloudflare ? (
                    // The certificate line from the fresh read (spec 04 6.14.10: an uncovered host name's message).
                    <span className="text-fg-muted" data-testid={`ep-cf-certificate-${d.hostname}`}>
                      {status.messages.find(
                        (m) => m.includes('edge certificate') && m.includes(d.hostname),
                      ) ?? e.cloudflare.edgeCertificateLine}
                    </span>
                  ) : (
                    <DomainNext
                      domain={d}
                      zone={(ep.domains ?? []).find((x) => x.hostname === d.hostname)?.dnsZone ?? null}
                      gcp={gcp}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="net-ep-heading">{e.routes}</h3>
      {status.routes.length === 0 ? (
        <p className="help">{e.noRoutes}</p>
      ) : (
        <table className="table table-cards net-ep-table">
          <caption className="sr-only">{e.routesCaption(status.entryPointId)}</caption>
          <thead>
            <tr>
              <th scope="col">{e.hostAndPath}</th>
              <th scope="col">{e.origin}</th>
              <th scope="col">{e.originAccess}</th>
              <th scope="col">{e.health}</th>
            </tr>
          </thead>
          <tbody>
            {status.routes.flatMap((r) =>
              hostPaths(r, status.hostName).map((hp) => (
                <tr key={`${r.routeId}-${hp}`} data-testid={`ep-route-${r.routeId}`}>
                  <td data-label={e.hostAndPath}>
                    <code className="font-mono text-[12.5px] break-all">{hp}</code>
                  </td>
                  <td data-label={e.origin}>
                    <Origin route={r} />
                  </td>
                  <td data-label={e.originAccess}>
                    <Access route={r} />
                    <OriginDomain route={r} />
                  </td>
                  <td data-label={e.health}>
                    <Health
                      route={r}
                      network={network}
                      pending={pending.has(r.routeId)}
                      messages={status.messages}
                      cloudflare={cloudflare}
                    />
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
      <Callouts status={status} network={network} />
    </>
  );
}

export function EntryPointCard({
  network,
  ep,
  kind,
  expanded,
  onToggle,
  manage,
  onEdit,
  onRotate,
  onRemove,
}: {
  network: Network;
  ep: EntryPointSettings;
  kind: EdgeKind | undefined;
  expanded: boolean;
  onToggle: () => void;
  manage: boolean;
  onEdit: () => void;
  onRotate: () => void;
  onRemove: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const status = useEntryPointStatus(network.id, ep.id);
  const [refreshing, setRefreshing] = useState(false);
  const s = status.data;
  const chips = addOnChips(ep, kind);
  // Feature spec 17, phase 5: the commitment add-ons' chips with the subscription's state and renewal.
  const commitmentChips = (kind?.addOns ?? [])
    .filter((a) => a.commitment && addOnEnabled(ep, kind, a.id))
    .map((a) => {
      const c = s?.commitments?.find((x) => x.addOn === a.id);
      const state = c?.state ?? 'waiting-for-apply';
      // Feature spec 17, phase 7: Cloud Armor Enterprise Annual's term and renewal are the billing account's.
      if (a.id === 'cloud-armor-enterprise-annual')
        return {
          id: a.id,
          text: `${e.gcp.enterpriseChip} · ${e.gcp.enterpriseState[state] ?? e.gcp.enterpriseState['waiting-for-apply']}`,
          tone: state === 'waiting-for-apply' ? 'neutral' : 'success',
        };
      // Whether it renews (user decision 2026-11-03): `renewsAt` while active, else the saved setting.
      const renewsAt = c?.renewsAt ?? null;
      const renews = state === 'active' ? !!renewsAt || !!c?.autoRenew : !!ep.addOns?.[a.id]?.autoRenew;
      const stateText =
        state === 'active'
          ? renewsAt
            ? e.aws.shieldState.renews(dayWords(renewsAt))
            : `${e.aws.shieldState.active(c?.endsAt ? dayWords(c.endsAt) : '')} · ${renews ? e.aws.renews : e.aws.noRenewal}`
          : state === 'active-elsewhere'
            ? e.aws.shieldState['active-elsewhere'](network.name)
            : `${e.aws.shieldState['waiting-for-apply']} · ${renews ? e.aws.renews : e.aws.noRenewal}`;
      return {
        id: a.id,
        text: `${e.aws.shieldChip} · ${stateText}`,
        tone: state === 'waiting-for-apply' ? 'neutral' : 'success',
      };
    });
  const edge =
    s?.hostName ?? s?.publicIp ?? (ep.kind === 'gcp-global-load-balancer' ? s?.addresses?.[0] : null) ?? null;
  const secretRoutes = (s?.routes ?? []).some(
    (r) => r.access === 'header' || r.access === 'allow-list-and-header',
  );
  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await api.getEntryPointStatus(network.id, ep.id, true);
      queryClient.setQueryData(queryKeys.entryPointStatus(network.id, ep.id), fresh);
    } catch (error) {
      toast.error(error);
    } finally {
      setRefreshing(false);
    }
  };
  const entries: MenuEntry[] = [
    { kind: 'action', id: 'edit', label: e.edit, onSelect: onEdit },
    ...(secretRoutes ? [{ kind: 'action' as const, id: 'rotate', label: e.rotate, onSelect: onRotate }] : []),
    { kind: 'action', id: 'refresh', label: e.refresh, onSelect: () => void refresh() },
    { kind: 'separator', id: 'sep' },
    { kind: 'action', id: 'remove', label: e.remove, onSelect: onRemove },
  ];
  const first = s?.routes[0];
  const bodyId = `ep-body-${ep.id}`;
  return (
    <article
      className={`net-ep-card ${expanded ? 'is-expanded' : ''}`}
      aria-labelledby={`ep-title-${ep.id}`}
      data-testid={`entry-point-${ep.id}`}
    >
      <header className="net-ep-head">
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          aria-expanded={expanded}
          aria-controls={expanded ? bodyId : undefined}
          aria-label={expanded ? e.collapse(ep.id) : e.expand(ep.id)}
          onClick={onToggle}
        >
          {expanded ? (
            <ChevronDown aria-hidden className="size-4" />
          ) : (
            <ChevronRight aria-hidden className="size-4" />
          )}
        </button>
        <span aria-hidden className="net-edge-abbr">
          {kind?.abbr ?? ep.kind.slice(0, 3).toUpperCase()}
        </span>
        <h3 id={`ep-title-${ep.id}`} className="font-semibold">
          {ep.id}
        </h3>
        <span className="text-fg-muted">{kindLine(ep, kind)}</span>
        <span className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <span
              key={chip.id}
              className={`badge tone-${chip.tone}`}
              title={chip.hint}
              data-testid={`ep-chip-${chip.id}`}
            >
              {chip.tone === 'success' ? (
                <ShieldCheck aria-hidden className="size-3.5" />
              ) : chip.tone === 'warning' ? (
                <TriangleAlert aria-hidden className="size-3.5" />
              ) : null}
              {chip.text}
              {chip.hint && <span className="sr-only">: {chip.hint}</span>}
            </span>
          ))}
          {commitmentChips.map((chip) => (
            <span
              key={chip.id}
              className={`badge tone-${chip.tone} whitespace-normal`}
              data-testid={`ep-chip-${chip.id}`}
            >
              <ShieldCheck aria-hidden className="size-3.5 shrink-0" />
              {chip.text}
            </span>
          ))}
          {s?.cloudflare && (
            // Feature spec 17, phase 8: SSL Full (strict) for the entry point's host names only.
            <span className="badge tone-neutral" data-testid="ep-chip-cloudflare-ssl">
              <Lock aria-hidden className="size-3.5" />
              {e.cloudflare.sslChip}
            </span>
          )}
          {s && !s.applied && <span className="badge tone-neutral">{e.notApplied}</span>}
          {s?.changedSinceApply && <span className="badge tone-warning">{e.changed}</span>}
        </span>
        {!expanded && first && (
          <span className="net-ep-oneline">
            <code className="font-mono text-[12px]">
              {hostPaths(first, s?.hostName ?? null)[0]} → {first.origin?.deploymentName ?? ''}
              {first.origin ? ` / ${first.origin.resourceId}` : ''}
            </code>
            <span className="text-[12px] text-fg-muted">
              {(ep.kind === 'cloudflare' && e.cloudflare.accessLabel[first.access]) ||
                e.accessLabel[first.access] ||
                first.access}
            </span>
            <Health
              route={first}
              network={network}
              messages={s?.messages}
              cloudflare={ep.kind === 'cloudflare'}
            />
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {expanded && edge && (
            <>
              <code className="font-mono text-[12.5px] text-fg-muted">{edge}</code>
              <CopyButton value={edge} label={s?.hostName ? e.copyHost : e.copyIp} />
            </>
          )}
          {refreshing && <Loader aria-hidden className="size-4 animate-spin" />}
          {manage && (
            <MenuButton
              buttonContent={<MoreHorizontal aria-hidden className="size-4" />}
              buttonLabel={e.actions(ep.id)}
              buttonClassName="btn btn-icon"
              menuLabel={e.actions(ep.id)}
              entries={entries}
              placement="below-end"
              portal
            />
          )}
        </span>
      </header>
      {expanded && (
        <div id={bodyId} className="net-ep-body">
          {status.error ? (
            <ErrorMessage error={status.error} onRetry={() => void status.refetch()} />
          ) : !s ? (
            <Loading />
          ) : (
            <Tables status={s} network={network} ep={ep} />
          )}
        </div>
      )}
    </article>
  );
}
